import {
    ActivityEventBindType,
    IActivityEventBindChild,
    IAlertResponse,
    IAnalyticsModelResponse,
    IIndicatorResponse,
    ITreeItemResponse,
} from '@kcftech/prometheus-client';
import { skipToken, useQuery } from '@tanstack/react-query';
import { usePermissions } from 'contexts/PermissionsContext';
import { sdClient } from 'contexts/SDKClientContext';
import { excludeNullAndUndefined } from 'utils/array';

const getBindIdsOfType = (
    binds: IActivityEventBindChild[],
    type: ActivityEventBindType,
): string[] =>
    excludeNullAndUndefined(
        binds.filter(({ BindType }) => BindType === type).map(({ BindId }) => BindId),
    );

export type ActivityEventBinds = Readonly<{
    groups: ITreeItemResponse[];
    metrics: IIndicatorResponse[];
    evidenceMetrics: IIndicatorResponse[];
    models: IAnalyticsModelResponse[];
    alert: IAlertResponse | undefined;
}>;

export const loadGroupBinds = (
    binds: IActivityEventBindChild[],
): Promise<ITreeItemResponse[]> =>
    Promise.all(
        getBindIdsOfType(binds, ActivityEventBindType.Group).map((id) =>
            sdClient.getTreeItem({ treeItemId: id }),
        ),
    );

export const loadMetricBinds = (
    binds: IActivityEventBindChild[],
): Promise<IIndicatorResponse[]> =>
    Promise.all(
        getBindIdsOfType(binds, ActivityEventBindType.Indicator).map((id) =>
            sdClient.getIndicator({ indicatorId: id }),
        ),
    );

export const loadEvidenceMetricBinds = (
    binds: IActivityEventBindChild[],
): Promise<IIndicatorResponse[]> =>
    Promise.all(
        getBindIdsOfType(binds, ActivityEventBindType.EvidenceIndicator).map((id) =>
            sdClient.getIndicator({ indicatorId: id }),
        ),
    );

export const loadModelBinds = (
    binds: IActivityEventBindChild[],
): Promise<IAnalyticsModelResponse[]> =>
    Promise.all(
        getBindIdsOfType(binds, ActivityEventBindType.Model).map((id) =>
            sdClient.getAnalyticsModel({ modelId: id }),
        ),
    );

export const loadAlertBind = async (
    binds: IActivityEventBindChild[],
): Promise<IAlertResponse | undefined> => {
    const alertId = binds.find(
        ({ BindType }) => BindType === ActivityEventBindType.Alert,
    )?.BindId;

    return alertId ? sdClient.getAlert({ alertId }) : undefined;
};

export function useActivityEventBinds(binds: IActivityEventBindChild[] | undefined) {
    const { canViewModel } = usePermissions();

    return useQuery<ActivityEventBinds | undefined>({
        queryKey: ['activity-event-binds', binds],

        queryFn: binds
            ? async () => ({
                  groups: await loadGroupBinds(binds),
                  metrics: await loadMetricBinds(binds),
                  evidenceMetrics: await loadEvidenceMetricBinds(binds),
                  models: canViewModel ? await loadModelBinds(binds) : [],
                  alert: await loadAlertBind(binds),
              })
            : skipToken,
    });
}
